<template>
  <main class="visit-review-page">
    <section class="visit-review-card">
      <h1>Oceń wizytę</h1>
      <p class="description">
        Podziel się swoją opinią po zakończonej wizycie. Twoja ocena pomoże nam ulepszać jakość usług.
      </p>

      <div v-if="!token" class="message error">
        Link do opinii jest nieprawidłowy lub nie zawiera tokenu.
      </div>

      <form v-else @submit.prevent="submitReview">
        <div class="field">
          <label>Twoja ocena</label>

          <div class="stars" role="radiogroup" aria-label="Ocena wizyty">
            <button
                v-for="star in maxRating"
                :key="star"
                type="button"
                class="star"
                :class="{ active: star <= rating }"
                :aria-label="`Wystaw ocenę ${star} z ${maxRating}`"
                @click="setRating(star)"
            >
              ★
            </button>
          </div>

          <p class="rating-info" v-if="rating">
            Wybrana ocena: {{ rating }}/{{ maxRating }}
          </p>
        </div>

        <div class="field">
          <label for="comment">Komentarz do wizyty</label>
          <div>
            <textarea
                id="comment"
                v-model.trim="comment"
                maxlength="2000"
                rows="6"
                placeholder="Napisz, jak przebiegła wizyta..."
            ></textarea>
          </div>

          <p class="counter">
            {{ comment.length }}/2000
          </p>
        </div>

        <div v-if="errorMessage" class="message error">
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="message success">
          {{ successMessage }}
        </div>

        <button
            type="submit"
            class="submit-button"
            :disabled="loading || !rating"
        >
          {{ loading ? 'Wysyłanie...' : 'Wyślij opinię' }}
        </button>
      </form>
    </section>
  </main>
</template>

<script>
import axios from 'axios';
import { useMeta } from 'vue-meta';

export default {
  name: 'VisitReview',

  setup() {
    useMeta({ title: 'Ocena wizyty' });
  },

  data() {
    return {
      maxRating: 10,
      rating: 0,
      comment: '',
      loading: false,
      errorMessage: '',
      successMessage: ''
    };
  },

  computed: {
    token() {
      return this.$route.query.token || '';
    }
  },

  methods: {
    setRating(rating) {
      this.rating = rating;
      this.errorMessage = '';
    },

    async submitReview() {
      this.errorMessage = '';
      this.successMessage = '';

      if (!this.token) {
        this.errorMessage = 'Brakuje tokenu opinii.';
        return;
      }

      if (!this.rating) {
        this.errorMessage = 'Wybierz ocenę wizyty.';
        return;
      }

      this.loading = true;

      try {
        await axios.post('/ReviewVisit', {
          token: this.token,
          rating: this.rating,
          comment: this.comment || null
        });

        this.successMessage = 'Dziękujemy! Twoja opinia została wysłana.';
        this.rating = 0;
        this.comment = '';
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },

    getErrorMessage(error) {
      if (
          error.response &&
          error.response.data &&
          error.response.data.message
      ) {
        return error.response.data.message;
      }

      return 'Nie udało się wysłać opinii. Spróbuj ponownie później.';
    }
  }
};
</script>

<style scoped lang="scss">
.visit-review-page {
  min-height: 70vh;
  padding: 60px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #f7f8fb;
}

.visit-review-card {
  max-width: 640px;
  padding: 36px;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.08);

  h1 {
    margin: 0 0 12px;
    font-size: 32px;
    color: #222222;
  }

  .description {
    margin: 0 0 28px;
    color: #666666;
    line-height: 1.5;
  }
}

.field {
  margin-bottom: 24px;

  label {
    display: block;
    margin-bottom: 10px;
    font-weight: 600;
    color: #222222;
  }

  textarea {
    box-sizing: border-box;
    resize: vertical;
    width: 100%;
    padding: 14px 16px;
    border: 1px solid #d8dce5;
    border-radius: 12px;
    font-family: inherit;
    font-size: 15px;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: #10b981;
    }
  }
}

.stars {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.star {
  border: none;
  background: transparent;
  color: #d5d9e2;
  font-size: 38px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover,
  &.active {
    color: #ffc107;
  }

  &:hover {
    transform: scale(1.08);
  }
}

.rating-info,
.counter {
  margin: 8px 0 0;
  color: #777777;
  font-size: 14px;
}

.counter {
  text-align: right;
}

.message {
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;

  &.error {
    color: #9f1d1d;
    background: #fdeaea;
  }

  &.success {
    color: #176b37;
    background: #e8f8ee;
  }
}

.submit-button {
  width: 100%;
  min-height: 48px;
  border: none;
  border-radius: 12px;
  background: #10b981;
  color: #ffffff;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background: #10b981;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

@media (max-width: 576px) {
  .visit-review-page {
    padding: 32px 12px;
  }

  .visit-review-card {
    padding: 24px;

    h1 {
      font-size: 26px;
    }
  }

  .star {
    font-size: 34px;
  }
}
</style>